import React from 'react';
import { motion } from 'framer-motion';
import { SectionNumber, BotanicalDivider } from './Decorative';
import { useLanguage } from '../i18n/LanguageContext';
export function WhatAreConstellations() {
  const {
    t
  } = useLanguage();
  return <section id="que-son" className="py-16 sm:py-20 md:py-32 px-4 sm:px-6 relative bg-sage-lighter/10" data-id="element-822">
      <div className="max-w-4xl mx-auto relative z-10" data-id="element-823">
        {/* Top Quote */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="text-center mb-16" data-id="element-824">
          <p className="text-3xl md:text-4xl font-serif text-gold-dark italic mb-4" data-id="element-825">
            "{t('whatare.top_quote')}"
          </p>
          <p className="text-forest-dark uppercase tracking-widest text-sm font-medium" data-id="element-826">
            — Bert Hellinger
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="text-center mb-12 sm:mb-20" data-id="element-827">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-forest-dark mb-6 uppercase leading-tight" data-id="element-828">
            {t('whatare.title')}
          </h2>
          <div className="w-24 h-[1px] bg-terracotta mx-auto mb-10" data-id="element-829"></div>
        </motion.div>

        {(() => {
        const paragraphs = t('whatare.paragraphs') as string[];
        const midpoint = Math.floor(paragraphs.length / 2);
        const firstHalf = paragraphs.slice(0, midpoint);
        const secondHalf = paragraphs.slice(midpoint);
        return <div className="space-y-12" data-id="element-830">
              {/* Top photo */}
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 1
          }} className="relative aspect-[16/9] rounded-[2.5rem] overflow-hidden shadow-xl border border-sage-light/30" data-id="element-831">
                <img src="https://cdn.magicpatterns.com/uploads/kXtdhCwe4RW7ZrkLwwBSHD/foto_3.jpg" alt="Taller de constelaciones familiares en grupo" className="w-full h-full object-cover" data-id="element-832" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/30 to-transparent" data-id="element-833"></div>
              </motion.div>

              {/* First half of paragraphs */}
              <div className="space-y-8 text-lg md:text-xl text-forest/80 leading-relaxed font-light bg-white/60 backdrop-blur-sm p-8 md:p-16 rounded-[3rem] shadow-sm border border-sage-light/20" data-id="element-834">
                {firstHalf.map((p: string, index: number) => <motion.p key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className={index === 0 ? 'font-medium text-forest-dark text-xl' : ''} data-id="element-835">
                    {p}
                  </motion.p>)}
              </div>

              {/* Difficulties Box */}
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 1
          }} className="bg-white rounded-[1.5rem] sm:rounded-[2rem] border-l-4 border-forest-dark p-6 sm:p-8 md:p-12 shadow-lg my-12" data-id="element-836">
                <p className="text-xl md:text-2xl font-serif text-forest-dark mb-8" data-id="element-837">
                  {t('whatare.difficulties_title')}
                </p>
                <div className="grid md:grid-cols-2 gap-4" data-id="element-838">
                  {(t('whatare.difficulties') as string[]).map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-839">
                      <span className="text-gold mt-1" data-id="element-840">✦</span>
                      <span className="text-forest/80 text-lg" data-id="element-841">{item}</span>
                    </div>)}
                </div>
              </motion.div>

              {/* Inspirational photo with pull quote */}
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 1
          }} className="relative rounded-[3rem] overflow-hidden shadow-2xl" data-id="element-842">
                <div className="aspect-[16/9] md:aspect-[21/9] relative" data-id="element-843">
                  <img src="https://cdn.magicpatterns.com/uploads/vxiQ2JMKCGUfBcspYHkA8N/FOTO_INSPIRADORA.jpg" alt="Atardecer en Casa Cal Faro" className="w-full h-full object-cover" data-id="element-844" />
                  <div className="absolute inset-0 bg-gradient-to-r from-forest-dark/70 via-forest-dark/30 to-transparent" data-id="element-845"></div>
                  <div className="absolute inset-0 flex items-center p-10 md:p-20" data-id="element-846">
                    <p className="text-2xl md:text-4xl lg:text-5xl font-serif text-cream italic leading-snug max-w-2xl drop-shadow-lg" data-id="element-847">
                      {t('whatare.overlay')}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Second half of paragraphs */}
              <div className="space-y-8 text-lg md:text-xl text-forest/80 leading-relaxed font-light bg-white/60 backdrop-blur-sm p-8 md:p-16 rounded-[3rem] shadow-sm border border-sage-light/20" data-id="element-848">
                {secondHalf.map((p: string, index: number) => <motion.p key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className={index === secondHalf.length - 1 ? 'font-medium text-forest-dark' : ''} data-id="element-849">
                    {p}
                  </motion.p>)}
              </div>

              {/* After Feelings Box */}
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 1
          }} className="bg-white rounded-[1.5rem] sm:rounded-[2rem] border-l-4 border-forest-dark p-6 sm:p-8 md:p-12 shadow-lg my-12" data-id="element-850">
                <p className="text-xl md:text-2xl font-serif text-forest-dark mb-8" data-id="element-851">
                  {t('whatare.after_title')}
                </p>
                <div className="grid md:grid-cols-2 gap-4" data-id="element-852">
                  {(t('whatare.after') as string[]).map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-853">
                      <span className="text-gold mt-1" data-id="element-854">✦</span>
                      <span className="text-forest/80 text-lg" data-id="element-855">{item}</span>
                    </div>)}
                </div>
              </motion.div>

              {/* Georgina Quote & Contact Button */}
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 1
          }} className="text-center mt-20" data-id="element-856">
                <p className="text-2xl md:text-3xl font-serif text-gold-dark italic mb-6" data-id="element-857">
                  "{t('whatare.georgina_quote')}"
                </p>
                <p className="text-sage-dark uppercase tracking-widest text-sm mb-10" style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 300
            }} data-id="element-858">
                  — Georgina Fahle Ruiz
                </p>
                <a href="#contacto" className="inline-block px-10 py-4 border-2 border-gold text-gold-dark font-bold tracking-widest uppercase rounded-full hover:bg-gold hover:text-white transition-colors" data-id="element-859">
                  {t('nav.contact')}
                </a>
              </motion.div>
            </div>;
      })()}

        <BotanicalDivider data-id="element-860" />
      </div>
    </section>;
}